import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, MapPin, Phone, Mail, Clock } from "lucide-react";
import { BUSINESS, ASSETS } from "../lib/assets";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome to the pit.");
      setEmail("");
    } catch {
      toast.error("Couldn't sign you up. Try again in a bit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative z-40 border-t border-ink bg-ink text-bone" data-testid="site-footer">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 py-16 md:grid-cols-12 md:px-8">
        <div className="md:col-span-4">
          <img src={ASSETS.logo} alt="When Pigs Fly Family BBQ" className="mb-6 h-24 w-auto invert" />
          <p className="font-serif text-sm leading-relaxed text-bone/80">
            Family-owned, slow-smoked BBQ from Spring Hill, Florida. Serving the Nature Coast &
            catering weddings, corporate events, and family reunions across Pasco, Hernando,
            Citrus, Northern Pinellas and Hillsborough counties.
          </p>
          <div className="mt-6 flex gap-3">
            <a href={BUSINESS.socials.facebook} target="_blank" rel="noreferrer" data-testid="footer-social-facebook" className="border border-bone p-2 transition-colors duration-200 hover:bg-bone hover:text-ink">
              <Facebook size={16} />
            </a>
            <a href={BUSINESS.socials.instagram} target="_blank" rel="noreferrer" data-testid="footer-social-instagram" className="border border-bone p-2 transition-colors duration-200 hover:bg-bone hover:text-ink">
              <Instagram size={16} />
            </a>
            <a href={BUSINESS.socials.youtube} target="_blank" rel="noreferrer" data-testid="footer-social-youtube" className="border border-bone p-2 transition-colors duration-200 hover:bg-bone hover:text-ink">
              <Youtube size={16} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow mb-4 text-bone/60">The Pit</div>
          <ul className="space-y-2 font-serif text-sm">
            <li><Link to="/menu" className="hover:link-underline">Menu &amp; Ordering</Link></li>
            <li><Link to="/seasonings" className="hover:link-underline">Seasonings &amp; Merch</Link></li>
            <li><Link to="/catering" className="hover:link-underline">Catering &amp; Weddings</Link></li>
            <li><Link to="/events" className="hover:link-underline">Awards &amp; Events</Link></li>
            <li>
              <Link to="/small-town-showdown" className="inline-flex items-center gap-2 hover:link-underline" data-testid="footer-small-town-showdown">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
                Small Town Showdown
              </Link>
            </li>
            <li><Link to="/gallery" className="hover:link-underline">Gallery</Link></li>
            <li><Link to="/blog" className="hover:link-underline">Blog</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow mb-4 text-bone/60">Visit</div>
          <ul className="space-y-3 font-serif text-sm">
            <li className="flex gap-2"><MapPin size={16} className="mt-1 shrink-0" /><span>{BUSINESS.street}<br />{BUSINESS.city}</span></li>
            <li className="flex gap-2"><Phone size={16} className="mt-1 shrink-0" /><a href={`tel:${BUSINESS.phoneRaw}`} className="hover:link-underline">{BUSINESS.phone}</a></li>
            <li className="flex gap-2"><Mail size={16} className="mt-1 shrink-0" /><a href={`mailto:${BUSINESS.email}`} className="link-underline break-all" data-testid="footer-email-link">{BUSINESS.email}</a></li>
          </ul>
          <div className="mt-5 border-t border-bone/20 pt-4">
            <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-bone/60">
              <Clock size={12} /> Hours
            </div>
            <table className="w-full font-mono text-[11px]" data-testid="footer-hours">
              <tbody>
                {BUSINESS.hours.map(([day, time]) => (
                  <tr key={day} className="border-b border-bone/10 last:border-b-0">
                    <td className="py-1.5 uppercase text-bone/60">{day}</td>
                    <td className="py-1.5 text-right text-bone">{time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.24em] text-bone/50">Holidays may vary</div>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow mb-4 text-bone/60">Get Smoke Signals</div>
          <p className="mb-3 font-serif text-sm text-bone/80">
            News, events, recipes and the occasional secret sauce.
          </p>
          <form onSubmit={onSubmit} className="flex" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="newsletter-email-input"
              className="w-full border border-bone bg-transparent px-3 py-2 font-mono text-xs uppercase text-bone placeholder:text-bone/40 focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-btn"
              className="border border-bone bg-bone px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-200 hover:bg-ink hover:text-bone disabled:opacity-60"
            >
              {loading ? "..." : "Join"}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-bone/20">
        <div className="mx-auto flex max-w-[1400px] flex-col justify-between gap-2 px-4 py-5 md:flex-row md:px-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">
            © {new Date().getFullYear()} When Pigs Fly Family BBQ
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">
            Spring Hill · Pasco County · Nature Coast, FL
          </p>
        </div>
      </div>

      <div className="border-t border-bone/20 bg-ink">
        <div className="mx-auto max-w-[1400px] px-4 py-4 text-center md:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-bone" data-testid="footer-credit">
            All in One Restaurant System Created by{" "}
            <a
              href="https://www.gregmiceli.com"
              target="_blank"
              rel="noreferrer"
              className="link-underline text-bone hover:text-bone"
            >
              www.GregMiceli.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
